import {createSlice, type PayloadAction} from '@reduxjs/toolkit';
import type {EventReminderVO, ManageEventPageReduxState, MobileViewManageType, NestedViewManageType} from '@Components/mystuff-manage-event-page/mystuff-manage-event-page.types';
import {getDummyEventItem} from '@Libraries/events.library';
import {LoadingStates} from '@Utils/loading.util';
import type {EventVO} from '@Components/events/events.types';
import type {EventRegistrationFields, EventRegistrations} from '@Components/event-wizard/event-wizard-registrations/event-wizard-registrations.types';
import type {EventStats} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';

const sortEventReminderEmails = (eventReminderEmails: EventReminderVO[]): EventReminderVO[] => {
  return eventReminderEmails.sort((a, b) => {
    const dateA = new Date(a.scheduledDateTimeInUTC);
    const dateB = new Date(b.scheduledDateTimeInUTC);
    return dateA.getTime() - dateB.getTime();
  });
};

const initialState: ManageEventPageReduxState = {
  event: getDummyEventItem(),
  eventRegistrations: [],
  eventRegistrationFields: {
    areRegistrationsRequired: true,
    email: true,
    name: false,
    phone: false,
    registerBtnText: '',
  },
  filteredEventRegistrationsFromSubnav: undefined,
  eventReminderEmails: [],
  eventStats: {
    views: 0,
    registrations: 0,
    reach: 0,
  },
  signupFormHTML: '',
  loadingState: LoadingStates.LOADING,
  mobileViewManageType: undefined,
  nestedViewManageType: undefined,
};

const manageEventPageSlice = createSlice({
  name: 'manageEventPageSlice',
  initialState,
  reducers: {
    onManageEventPageEventChange: (state, action: PayloadAction<EventVO>): void => {
      state.event = action.payload;
    },
    onManageEventPageRegistrationsChange: (state, action: PayloadAction<EventRegistrations[]>): void => {
      state.eventRegistrations = action.payload;
    },
    onManageEventPageFilteredRegistrationsFromSubnavChange: (state, action: PayloadAction<EventRegistrations[] | undefined>): void => {
      state.filteredEventRegistrationsFromSubnav = action.payload;
    },
    onManageEventPageRegistrationFieldChange: (state, action: PayloadAction<EventRegistrationFields>): void => {
      state.eventRegistrationFields = action.payload;
    },
    onManageEventPageEventStatsChange: (state, action: PayloadAction<EventStats>): void => {
      state.eventStats = action.payload;
    },
    onManageEventPageReminderEmailsChange: (state, action: PayloadAction<EventReminderVO[]>): void => {
      state.eventReminderEmails = action.payload;
    },
    onManageEventPageNewReminder: (state, action: PayloadAction<EventReminderVO>): void => {
      state.eventReminderEmails.push(action.payload);
      state.eventReminderEmails = sortEventReminderEmails(state.eventReminderEmails);
    },
    onManageEventPageUpdateReminder: (state, action: PayloadAction<EventReminderVO>): void => {
      state.eventReminderEmails = state.eventReminderEmails.filter((reminder) => reminder.emailMarketingCampaignHID !== action.payload.emailMarketingCampaignHID);
      state.eventReminderEmails.push(action.payload);
      state.eventReminderEmails = sortEventReminderEmails(state.eventReminderEmails);
    },
    onManageEventPageDeleteReminder: (state, action: PayloadAction<EventReminderVO>): void => {
      state.eventReminderEmails = state.eventReminderEmails.filter((reminder) => reminder.emailMarketingCampaignHID !== action.payload.emailMarketingCampaignHID);
    },
    onManageEventPageSignupFormHTMLChange: (state, action: PayloadAction<string | undefined>): void => {
      state.signupFormHTML = action.payload;
    },
    onManageEventPageMailingListAttendeeUpdate: (state, action: PayloadAction<EventRegistrations>): void => {
      const newRegistration: EventRegistrations = action.payload;
      state.eventRegistrations = state.eventRegistrations.map((registration) =>
        registration.id === newRegistration.id && registration.idMailingList === newRegistration.idMailingList ? newRegistration : registration
      );
    },
    onManageEventPageMailingListAttendeeDelete: (state, action: PayloadAction<EventRegistrations>): void => {
      const deletedRegistration = action.payload;
      state.eventRegistrations = state.eventRegistrations.filter(
        (registration) => registration.id !== deletedRegistration.id || registration.idMailingList !== deletedRegistration.idMailingList
      );
    },
    onManageEventPageLoadingStateChange: (state, action: PayloadAction<LoadingStates>): void => {
      state.loadingState = action.payload;
    },
    onManageEventPageMobileViewManageTypeChange: (state, action: PayloadAction<MobileViewManageType>): void => {
      state.mobileViewManageType = action.payload;
    },
    onManageEventSwitchToDefaultMobileViewManageTypeChange: (state): void => {
      state.mobileViewManageType = undefined;
    },
    onManageEventPageNestedViewManageTypeChange: (state, action: PayloadAction<NestedViewManageType | undefined>): void => {
      state.nestedViewManageType = action.payload;
    },
    onManageEventPageSwitchToDefaultManageNestedView: (state): void => {
      state.nestedViewManageType = undefined;
    },
  },
});

export const {
  onManageEventPageEventChange,
  onManageEventPageRegistrationsChange,
  onManageEventPageFilteredRegistrationsFromSubnavChange,
  onManageEventPageRegistrationFieldChange,
  onManageEventPageReminderEmailsChange,
  onManageEventPageNewReminder,
  onManageEventPageUpdateReminder,
  onManageEventPageDeleteReminder,
  onManageEventPageSignupFormHTMLChange,
  onManageEventPageMailingListAttendeeUpdate,
  onManageEventPageMailingListAttendeeDelete,
  onManageEventPageLoadingStateChange,
  onManageEventPageEventStatsChange,
  onManageEventPageMobileViewManageTypeChange,
  onManageEventSwitchToDefaultMobileViewManageTypeChange,
  onManageEventPageNestedViewManageTypeChange,
  onManageEventPageSwitchToDefaultManageNestedView,
} = manageEventPageSlice.actions;

export const manageEventPageReducer = manageEventPageSlice.reducer;
